import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { TeacherDashboardData, GreetingData, ScheduleFile, ClassLevel } from '../pages/mini-apps/teacher-dashboard';

const PRIMARY_LEVEL_CODES = ['TPS', 'PS', 'MS', 'GS', 'CP', 'CE1', 'CE2', 'CM1', 'CM2'];

const NUMBERED_LEVEL_RE = /^(\d{1,2})\s*(ères?|eres?|res?|ndes?|nd|èmes?|emes?|e)/i;
const TERMINAL_LEVEL_RE = /^T(?:le|erm(?:inale)?)/i;

function numberedOrdinal(grade: string, suffix: string): string {
  if (grade === '1') return 'ère';
  if (/^n/i.test(suffix)) return 'nde';
  return 'e';
}

function parseLevelLabel(label: string): ClassLevel | null {
  const text = label.trim();
  if (!text) return null;

  const numbered = text.match(NUMBERED_LEVEL_RE);
  if (numbered) return { grade: numbered[1]!, ordinal: numberedOrdinal(numbered[1]!, numbered[2]!) };

  if (TERMINAL_LEVEL_RE.test(text)) return { grade: 'T', ordinal: 'le' };

  const upper = text.toUpperCase();
  const primary = PRIMARY_LEVEL_CODES.find((code) => upper.startsWith(code));
  if (primary) return { grade: primary, ordinal: '' };

  return null;
}

function classLevel(level: string | null, name: string): ClassLevel | null {
  if (level && level.trim()) return parseLevelLabel(level) ?? { grade: level.trim(), ordinal: '' };
  return parseLevelLabel(name);
}

function formatRelativeDate(iso: string): string {
  const now = Date.now();
  const diff = now - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  const hours = Math.floor(mins / 60);
  const days = Math.floor(hours / 24);
  if (days >= 1) return `Modifié il y a ${days} j`;
  if (hours >= 1) return `Modifié il y a ${hours} h`;
  return `Modifié il y a ${mins} min`;
}

const KIND_LABELS: Record<string, string> = {
  general: 'Document',
  course: 'Cours',
  exercice_sheet: 'Exercice',
  exam: 'Évaluation',
};

export function useTeacherDashboard(): {
  data: TeacherDashboardData | null;
  stats: { classroomsCount: number; activeAssignmentsCount: number; threadsCount: number } | null;
  loading: boolean;
  error: Error | null;
  uploadSchedule: (file: File) => Promise<void>;
} {
  const queryClient = useQueryClient();

  const dashboardQ = useQuery({
    queryKey: ['teacher', 'dashboard'],
    queryFn: () => api.teacher.dashboard.query(),
    staleTime: 60_000,
  });

  const draftsQ = useQuery({
    queryKey: ['teacher', 'draftDocuments'],
    queryFn: () => api.teacher.draftDocuments.query(),
    staleTime: 30_000,
  });

  const greetingQ = useQuery({
    queryKey: ['teacher', 'greeting', dashboardQ.data?.first_name ?? ''],
    queryFn: () =>
      api.teacher.greeting.query({ firstName: dashboardQ.data?.first_name ?? 'vous' }),
    staleTime: 3_600_000,
    enabled: dashboardQ.isSuccess,
  });

  const statsQ = useQuery({
    queryKey: ['teacher', 'stats'],
    queryFn: () => api.teacher.stats.query(),
    staleTime: 60_000,
  });

  const scheduleQ = useQuery({
    queryKey: ['teacher', 'scheduleFile'],
    queryFn: () => api.teacher.scheduleFile.query(),
    staleTime: 60_000,
  });

  const uploadUrlMut = useMutation({
    mutationFn: () => api.teacher.scheduleUploadUrl.mutate(),
  });

  const confirmMut = useMutation({
    mutationFn: (args: { path: string; fileName: string; fileSize: number }) =>
      api.teacher.scheduleConfirm.mutate(args),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teacher', 'scheduleFile'] });
    },
  });

  const uploadSchedule = useCallback(async (file: File) => {
    const { signed_url, path } = await uploadUrlMut.mutateAsync();
    await fetch(signed_url, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': 'application/pdf' },
    });
    await confirmMut.mutateAsync({ path, fileName: file.name, fileSize: file.size });
  }, [uploadUrlMut, confirmMut]);

  const loading = dashboardQ.isLoading || draftsQ.isLoading;
  const error = dashboardQ.error ?? draftsQ.error ?? null;

  const stats = statsQ.data
    ? { classroomsCount: statsQ.data.classroomsCount, activeAssignmentsCount: statsQ.data.activeAssignmentsCount, threadsCount: statsQ.data.threadsCount }
    : null;

  if (loading) return { data: null, stats, loading: true, error: null, uploadSchedule };
  if (error) {
    return { data: null, stats, loading: false, error: error instanceof Error ? error : new Error(String(error)), uploadSchedule };
  }
  if (!dashboardQ.data) return { data: null, stats, loading: false, error: null, uploadSchedule };

  const classes = (dashboardQ.data.classrooms ?? []).map((c) => ({
    id: c.id,
    name: c.name,
    level: classLevel(c.level_code, c.name),
    studentCount: c.student_count,
    subjects: c.subjects ?? [],
  }));

  const rows = (draftsQ.data ?? []).map((d) => ({
    id: d.id,
    title: d.title || `${KIND_LABELS[d.kind] ?? 'Document'} sans titre`,
    sub: formatRelativeDate(d.updated_at),
  }));

  const greetingData = greetingQ.data;
  const greeting: GreetingData | null = greetingData
    ? {
        timeMessage:    greetingData.time_message,
        quoteText:      greetingData.quote_text,
        quoteAuthor:    greetingData.quote_author,
        quoteAuthorRole: greetingData.quote_author_role,
        aiNote:         greetingData.ai_note,
      }
    : null;

  const sf = scheduleQ.data;
  const scheduleFile: ScheduleFile | null = sf
    ? {
        name:       sf.file_name,
        fileSize:   sf.file_size,
        signedUrl:  sf.signed_url,
        uploadedAt: sf.uploaded_at,
      }
    : null;

  return {
    loading: false,
    error: null,
    stats,
    uploadSchedule,
    data: {
      teacher:    { firstName: dashboardQ.data.first_name ?? 'vous' },
      greeting,
      rows,
      classes,
      scheduleFile,
    },
  };
}
